/**
 * Utility functions for the Aseos domain.
 *
 * Extracted from useAseosLogic.ts so they can be reused (e.g., in tests or
 * other pages that deal with raw cleaning records from the API).
 */

import dayjs from "dayjs";
import { Aseo, AseoRaw } from "../interfaces/api.interfaces";

/** Display labels indexed by the numeric `tipo` field from the API. */
export const PAGO_LABELS: Record<number, string> = {
  0: "Aloja Fácil",
  // tipo=1 = el Cliente-gestor (con login, /finanzas-cliente). Round 2026-06-20
  // (E3, decisión D-A): el dinero del aseo/flujo con tipo=1 va al Cliente, no al
  // dueño legal. Antes el label decía "Propietario".
  1: "Cliente",
  2: "Arrendatario",
  3: "Aseador",
  4: "Otro",
};

/**
 * ¿Este aseo es de la aseadora `userId` (responsable O acompañante del
 * reparto)? Round 2026-08-25 + review 2026-09-07: el predicado estaba copiado
 * en cuatro lugares de useAseosLogic y el próximo consumidor iba a copiar solo
 * la mitad legacy (`idAseador === userId`), volviendo a esconderle los aseos a
 * las acompañantes. `aseadorIds` ya trae el fallback legacy (mapRawAseo).
 */
export function esAseoDeLaAseadora(
  aseo: { idAseador?: number | null; aseadorIds?: number[] },
  userId: number | null | undefined,
): boolean {
  if (userId == null) return false;
  if (aseo?.idAseador === userId) return true;
  return (aseo?.aseadorIds ?? []).includes(userId);
}

/**
 * Semilla de los campos del editor de reparto (round 2026-08-25) a partir del
 * detalle de un aseo del backend (`d.aseadores` con montos para admin, o el
 * fallback legacy `d.idAseador`/`d.sueldoAseador`). La usan los DOS caminos de
 * hidratación del modal del calendario — el review 2026-09-07 los cazó como
 * dos copias de ~20 líneas que iban a envejecer por separado.
 */
export function seedEquipoAseo(d: {
  aseadores?: Array<{ usuario_id: number; monto?: number }> | null;
  idAseador?: number | null;
  sueldoAseador?: number | null;
}): {
  idAseadores: number[];
  asignaciones: Array<{ usuario_id: number; monto: number }>;
  repartoTocado: false;
} {
  const equipo = Array.isArray(d?.aseadores) ? d.aseadores : [];
  if (equipo.length > 0) {
    return {
      idAseadores: equipo.map((x) => Number(x.usuario_id)),
      asignaciones: equipo.map((x) => ({
        usuario_id: Number(x.usuario_id),
        monto: Number(x.monto) || 0,
      })),
      repartoTocado: false,
    };
  }
  if (d?.idAseador != null) {
    return {
      idAseadores: [Number(d.idAseador)],
      asignaciones: [
        { usuario_id: Number(d.idAseador), monto: Number(d.sueldoAseador) || 0 },
      ],
      repartoTocado: false,
    };
  }
  return { idAseadores: [], asignaciones: [], repartoTocado: false };
}

/**
 * Maps a raw API aseo record (AseoRaw) to the internal Aseo shape used by
 * the UI. Handles nullable fields and formats dates with dayjs.
 */
export function mapRawAseo(r: AseoRaw): Aseo {
  const pagoToLabel = (p: any) => {
    if (!p) return "";
    if (p.nombre && p.nombre !== PAGO_LABELS[p.tipo]) return p.nombre;
    return PAGO_LABELS[p.tipo] ?? p.nombre ?? "";
  };

  // Desglose resuelto de los 4 subítems para el prefill del modal (cascada
  // D-B). getAllCleanings ya lo entrega calculado (`subitems_resolved`); pero
  // las respuestas de create/update devuelven el aseo crudo (con
  // `subitems_override` pero sin `subitems_resolved`). En ese caso lo
  // construimos desde el override que acabamos de guardar — así re-abrir el
  // modal sin recargar la página no pierde transporte/materiales/lavandería.
  const rawOverride = (r as any).subitems_override;
  const subitemsResolved = r.subitems_resolved
    ? r.subitems_resolved
    : rawOverride
      ? {
          aseador: Number(rawOverride.aseador) || 0,
          transporte_total: Number(rawOverride.transporte_total) || 0,
          materiales_total: Number(rawOverride.materiales_total) || 0,
          lavanderia_total: Number(rawOverride.lavanderia_total) || 0,
        }
      : null;

  // Round 2026-08-25 — reparto multi-aseadora. `aseadores` viene de la API
  // (responsable primero, con nombre; monto solo para admin). Fallback legacy
  // (respuesta de create/update cruda o payload WS): `aseador_ids` o el
  // `id_usuario` suelto.
  const aseadores = Array.isArray((r as any).aseadores)
    ? ((r as any).aseadores as Array<{
        usuario_id: number;
        nombre?: string | null;
        monto?: number;
      }>)
    : Array.isArray((r as any).asignaciones)
      ? ((r as any).asignaciones as Array<{ usuario_id: number; monto?: number }>).map(
          (a) => ({ usuario_id: Number(a.usuario_id), monto: a.monto }),
        )
      : [];
  const aseadorIds: number[] = aseadores.length
    ? aseadores.map((a) => Number(a.usuario_id))
    : Array.isArray((r as any).aseador_ids)
      ? ((r as any).aseador_ids as number[]).map(Number)
      : r.id_usuario != null
        ? [Number(r.id_usuario)]
        : [];

  return {
    id: r.id,
    idAseador: r.id_usuario,
    nombreAseador: null,
    aseadores,
    aseadorIds,
    idPropiedad: r.id_propiedad ?? 0,
    propiedad: "",
    direccion: "",
    fechaInicio: r.fecha_inicio
      ? dayjs(r.fecha_inicio).format("YYYY-MM-DD")
      : "",
    fechaFin: r.fecha_termino
      ? dayjs(r.fecha_termino).format("YYYY-MM-DD")
      : "",
    horaInicio: r.hora_aseo?.slice(0, 5) ?? "",
    estado: r.estado,
    urgencia: r.urgencia,
    observaciones: r.observaciones ?? "",
    // Textos de las otras 2 notas (solo vienen para admin) → los usa el
    // buscador de notas en /aseos. Round 2026-07-06.
    notaPrivadaAdmin: (r as any).nota_privada_admin ?? "",
    notaAseador: (r as any).nota_aseador ?? "",
    costoLimpieza: r.costo_limpieza ?? 0,
    sueldoAseador: r.sueldo_aseador ?? 0,
    modoAsignacion: r.id_usuario != null ? "designado" : "abierto",
    asignacionActiva: r.id_usuario != null,
    aseadorasRechazados: [],
    quienPaga: pagoToLabel(r.pagado_por),
    sector: r.id_sector,
    // Backend devuelve `tareas` como Array<{ id, descripcion, imagenes }>
    // (objetos con detalle completo para el modal). Para compat con código
    // legacy que esperaba string[], aceptamos ambos shapes pero preferimos
    // el detallado.
    tareas: Array.isArray((r as any)?.tareas)
      ? ((r as any).tareas as Array<
          | string
          | {
              id?: number;
              descripcion?: string;
              imagenes?: Array<{
                url?: string;
                thumbUrl?: string;
                /** Versión liviana (480p) del video; `null` si no existe. */
                previewUrl?: string | null;
                kind?: string;
                originalName?: string;
                comentario?: string | null;
              }>;
            }
        >)
      : [],
    imagenes: Array.isArray((r as any)?.imagenes)
      ? ((r as any).imagenes as string[])
      : [],
    // Round 2026-06-19 (cascada D-B): vínculo al arriendo + desglose resuelto
    // de los 4 subítems para el prefill del modal de edición.
    reservaId: r.reserva_id ?? null,
    subitemsResolved,
  };
}
